const originalContent = {};

/**
 * Given a container, capture the original content.
 *
 * @param $container The container.
 */
function captureOriginalContent($container) {
	if (!$container.hasAttribute('id')) {
		console.error('No ID on container, container is required for tracking content.', {$container});
		return;
	}
	originalContent[$container.id] = $container.innerHTML;
}

/**
 * Given a container, restore the original content.
 *
 * @param $container The container.
 */
function restoreOriginalContent($container) {
	if (!$container.hasAttribute('id')) {
		console.error('No ID on container, container is required for tracking content.', {$container});
		return;
	}

	if (!originalContent[$container.id]) {
		console.error('No original content for container.', {$container});
		return;
	}

	$container.innerHTML = originalContent[$container.id];
}


/**
 * Given a container and a slug, attempt to load the breakdown for the given slug. This is very simplistic, in that
 * it essentially loads the same page with the corresponding query vars, pulls out the shortcode content
 * and replaces the container's content with what it found...
 *
 * @param $container The container we're working with.
 * @param breakdownSlug The slug of the breakdown to load.
 */
function loadBreakdownForTable($container, breakdownSlug) {
	if (!$container || !breakdownSlug) {
		console.warn('No container or slug', {$container, breakdownSlug});
		return;
	}

	const id = $container.id;

	// No id (this shouldn't happen, but just in case...)...
	if (!id) {
		return;
	}

	// Bail if loading already...
	if ($container.dataset.amsCpdLoading) {
		return;
	}

	$container.dataset.amsCpdLoading = 'true';

	// Get the current URL and replace/set the query var for this table id.
	const url = new URL(window.location.href);
	url.searchParams.delete(id);
	url.searchParams.append(id, breakdownSlug);

	// Fetch the URL and get the content.
	fetch(url.href)
		.then(response => response.text())
		.then(html => {
			const $html = new DOMParser().parseFromString(html, 'text/html');
			const $shortcodeOutput = $html.querySelector(`#${id}`);

			if (!$shortcodeOutput) {
				throw new Error('Unable to find shortcode output/element...');
			}

			// Replace the content.
			$container.innerHTML = $shortcodeOutput.innerHTML;
		})
		.catch(error => {
			console.log('Error fetching breakdown', {error});
			alert('Sorry, unable to load the breakdown for this table. Please try again later.')
		})
		.finally(() => {
			delete $container.dataset.amsCpdLoading;
		});
}

/**
 * Given a container and an anchor link, load the content from the anchor link and replace the container.
 *
 * @param {HTMLElement} $container The container with content to replace.
 * @param {HTMLAnchorElement} $anchor The anchor with the link to fetch.
 */
function loadContentForTableFromLink($container, $anchor) {
	if (!$container || !$anchor || $anchor.tagName !== 'A') {
		console.warn('No container or anchor', {$container, breakdownSlug});
		return;
	}

	const id = $container.id;

	// No id (this shouldn't happen, but just in case...)...
	if (!id) {
		return;
	}

	// Bail if loading already...
	if ($container.dataset.amsCpdLoading) {
		return;
	}

	$container.dataset.amsCpdLoading = 'true';

	// Get the current URL and replace/set the query var for this table id.
	const url = new URL($anchor.href);

	// Fetch the URL and get the content.
	fetch(url.href)
		.then(response => response.text())
		.then(html => {
			const $html = new DOMParser().parseFromString(html, 'text/html');
			const $shortcodeOutput = $html.querySelector(`#${id}`);

			if (!$shortcodeOutput) {
				throw new Error('Unable to find shortcode output/element...');
			}

			// Replace the content.
			$container.innerHTML = $shortcodeOutput.innerHTML;
		})
		.catch(error => {
			console.log('Error fetching other page', {error});
			alert('Sorry, unable to load the page for this table. Please try again later.')
		})
		.finally(() => {
			delete $container.dataset.amsCpdLoading;
		});
}

/**
 * Setup a table for viewing breakdowns of CPD types.
 *
 * @param {HTMLTableElement} $container The CPD container/table to setup.
 */
function setupCpdTable($container) {
	// Already been setup?
	if (!$container || $container.hasAttribute('data-ams-cpd-setup')) {
		return;
	}

	captureOriginalContent($container);

	$container.dataset.amsCpdSetup = 'true';

	// 1. Add the event which will handle reloading/etc the table for type breakdown.
	$container.addEventListener('click', event => {
		// Wasn't one of the anchors, bail...
		const $target = event.target;
		if ($target.tagName !== 'A') {
			return;
		}

		event.preventDefault();
		event.stopImmediatePropagation();


		// If it's the back link, restore the original content and bail.
		if ($target.classList.contains('ams-cpd-units-table__back-link')) {
			restoreOriginalContent($container);
			return;
		}

		const breakdownSlug = $target.closest('tr[data-ams-cpd-breakdown-slug]')?.dataset?.amsCpdBreakdownSlug;

		if (breakdownSlug) {
			loadBreakdownForTable($container, breakdownSlug);
			return;
		}

		// If it's a pagination link
		if ($target.classList.contains('ams-cpd-pager__page-link')) {
			loadContentForTableFromLink($container, $target);
		}
	});
}

document.addEventListener('DOMContentLoaded', event => {

	// Get all shortcode outputs, which have tables with IDs and rows which have type references. This is an optional
	// thing per shortcode, so not all shortcode output will support this.
	const $candidates = document.querySelectorAll('.ams-cpd-units-table[id]');

	if ($candidates.length === 0) {
		return;
	}

	$candidates.forEach($candidate => setupCpdTable($candidate));
});
